import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, Link2, Sparkles, Video, Users, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FirefliesSuggestionsPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/fireflies/suggestions`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      setData(j);
    } catch { toast.error("Error cargando sugerencias"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const linkTo = async (fireflies_id, project_id, project_name) => {
    setLinking(fireflies_id);
    try {
      const r = await fetch(`${API}/fireflies/transcripts/${fireflies_id}/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id }),
      });
      if (r.ok) {
        toast.success(`Vinculado a ${project_name}`);
        load();
      } else {
        toast.error(`Error al vincular: ${r.status}`);
      }
    } finally { setLinking(null); }
  };

  const markOps = async (fireflies_id, title) => {
    setLinking(fireflies_id);
    try {
      const r = await fetch(`${API}/fireflies/transcripts/${fireflies_id}/mark-ops`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_ops: true }),
      });
      if (r.ok) {
        toast.success(`"${title || fireflies_id}" marcada como reunión operativa (no requiere vinculación)`);
        load();
      } else {
        toast.error(`Error: ${r.status}`);
      }
    } finally { setLinking(null); }
  };

  const scoreColor = (s) => {
    if (s >= 0.9) return "text-emerald-700 bg-emerald-100";
    if (s >= 0.5) return "text-amber-700 bg-amber-100";
    return "text-slate-600 bg-slate-100";
  };

  return (
    <div className="space-y-6" data-testid="fireflies-suggestions-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Fireflies · Matching asistido</div>
        <h1 className="text-3xl font-black flex items-center gap-2"><Sparkles className="w-7 h-7 text-emerald-600" /> Vincula transcripciones a proyectos</h1>
        <p className="text-sm text-muted-foreground mt-1">Sugerencias automáticas por similitud de título con los proyectos PG-07. Confirma con un click.</p>
      </div>

      {loading ? <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
        <>
          <div className="text-sm text-muted-foreground" data-testid="fireflies-count">
            {data?.unlinked_count || 0} transcripciones sin proyecto asignado.
          </div>
          <div className="space-y-3">
            {(data?.suggestions || []).map((s, i) => (
              <Card key={s.fireflies_id} data-testid={`fireflies-item-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Video className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <div className="font-semibold truncate">{s.title || "(sin título)"}</div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {s.date && <span>{String(s.date).slice(0, 16).replace("T", " ")}</span>}
                        {s.duration_min != null && <span>{Math.round(s.duration_min)} min</span>}
                        {(s.participants || []).length > 0 && (
                          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{s.participants.length}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      className="text-xs text-slate-500 hover:text-slate-900"
                      disabled={linking === s.fireflies_id}
                      onClick={() => markOps(s.fireflies_id, s.title)}
                      data-testid={`fireflies-mark-ops-${i}`}
                      title="Reunión operativa/interna sin proyecto asociado"
                    >
                      {linking === s.fireflies_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                      Reunión ops
                    </Button>
                  </div>

                  {s.candidates?.length > 0 ? (
                    <div className="mt-3 space-y-1.5" data-testid={`fireflies-candidates-${i}`}>
                      {s.candidates.map((c, k) => (
                        <div key={c.project_id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800">
                          <Badge className={`text-[10px] px-2 ${scoreColor(c.score)}`}>score {(c.score * 100).toFixed(0)}%</Badge>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{c.project_name}</div>
                            {c.phase && <div className="text-[10px] text-muted-foreground">{c.phase}</div>}
                          </div>
                          <Button
                            size="sm" variant={k === 0 ? "default" : "outline"}
                            disabled={linking === s.fireflies_id}
                            onClick={() => linkTo(s.fireflies_id, c.project_id, c.project_name)}
                            data-testid={`fireflies-link-${i}-${k}`}
                          >
                            {linking === s.fireflies_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                            <span className="ml-1">Vincular</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-muted-foreground italic">Sin coincidencias automáticas. Vincula manualmente desde PG-07.</div>
                  )}
                </CardContent>
              </Card>
            ))}
            {!(data?.suggestions || []).length && (
              <Card><CardContent className="p-8 text-center text-muted-foreground">🎉 Todas las transcripciones están vinculadas.</CardContent></Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
